import React, { useState } from 'react';
import DatePicker from '../components/DatePicker';
import { reportsAPI } from '../services/api';
import { formatDate, getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { formatCoordinates } from '../utils/geolocation';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ReportPages.module.css';

const downloadCsv = (filename, rows) => {
  const escapeCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const ROLE_COLOR = {
  admin: styles.logUser_admin,
  supervisor: styles.logUser_supervisor,
  user: styles.logUser_user,
};

const UserReportPage = () => {
  const { t } = useLocalization();
  const [mode, setMode] = useState('monthly');
  const [reportType, setReportType] = useState('summary');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return toKualaLumpurISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    if (nextMode === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
    } else if (nextMode === 'monthly') {
      setStartDate(toKualaLumpurISODate(new Date(year, month, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, month + 1, 0)));
    } else if (nextMode === 'yearly') {
      setStartDate(toKualaLumpurISODate(new Date(year, 0, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, 11, 31)));
    }
    // 'range' keeps current dates
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.userActivityReport({ mode, startDate, endDate });
      setRows(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t('user_report_load_failed', 'Failed to load user activity report'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (reportType === 'summary') {
      const csvRows = [
        [
          t('users_username', 'Username'),
          t('users_role', 'Role'),
          t('teams_page_title', 'Teams'),
          t('dashboard_total_tasks', 'Total Tasks'),
          t('tasks_status_completed', 'Completed'),
          t('tasks_status_in_progress', 'In Progress'),
          t('tasks_status_pending', 'Pending'),
          t('tasks_status_cancelled', 'Cancelled'),
          t('user_report_completion_pct', 'Completion %')
        ],
        ...rows.map((row) => {
          const pct = row.total_tasks > 0 ? Math.round((Number(row.completed) / Number(row.total_tasks)) * 100) : 0;
          return [
            row.username,
            row.role,
            row.teams || '-',
            row.total_tasks,
            row.completed,
            row.in_progress,
            row.pending,
            row.cancelled,
            `${pct}%`,
          ];
        }),
      ];
      downloadCsv(t('user_report_summary_export_filename', 'user-activity-summary.csv'), csvRows);
      return;
    }

    // detail – flatten all tasks per user
    const csvRows = [
      [
        t('users_username', 'Username'),
        t('users_role', 'Role'),
        t('task_report_task_date', 'Task Date'),
        t('reports_task', 'Task'),
        t('tasks_label_status', 'Status'),
        t('tasks_label_priority', 'Priority'),
        t('tasks_label_job_type', 'Job Type'),
        t('tasks_label_team', 'Team'),
        t('common_location', 'Location'),
        t('common_gps', 'GPS')
      ],
    ];
    rows.forEach((user) => {
      if (!Array.isArray(user.tasks) || user.tasks.length === 0) {
        csvRows.push([
          user.username,
          user.role,
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
        ]);
      } else {
        user.tasks.forEach((task) => {
          csvRows.push([
            user.username,
            user.role,
            task.task_date,
            task.title,
            task.status,
            task.priority,
            task.job_type_name || '-',
            task.team_name || '-',
            task.task_day_location_name || '-',
            formatCoordinates(task.task_day_latitude, task.task_day_longitude) || '-',
          ]);
        });
      }
    });
    downloadCsv(t('user_report_detail_export_filename', 'user-activity-detail.csv'), csvRows);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('user_report_title', 'User Activity Report')}</h1>
        <p>{t('user_report_subtitle', 'Shows how active each user is across tasks for the selected period.')}</p>
      </div>

      <div className={styles.controls}>
        <div className={styles.modeButtons}>
          {['daily', 'monthly', 'yearly', 'range'].map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.modeBtn} ${mode === item ? styles.modeBtnActive : ''}`}
              onClick={() => handleModeChange(item)}
            >
              {item === 'daily'
                ? t('common_daily', 'Daily')
                : item === 'monthly'
                  ? t('common_monthly', 'Monthly')
                  : item === 'yearly'
                    ? t('common_yearly', 'Yearly')
                    : t('common_range', 'Range')}
            </button>
          ))}
        </div>

        <select className={styles.select} value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="summary">{t('reports_summary', 'Summary')}</option>
          <option value="detail">{t('reports_detail', 'Detail')}</option>
        </select>

        <DatePicker value={startDate} onChange={mode === 'range' ? setStartDate : undefined} placeholder={t('tasks_start_date', 'Start date')} className={styles.dateInput} disabled={mode !== 'range'} />
        <DatePicker value={endDate} onChange={mode === 'range' ? setEndDate : undefined} placeholder={t('tasks_end_date', 'End date')} className={styles.dateInput} disabled={mode !== 'range'} />

        <button className={styles.actionBtn} type="button" onClick={fetchReport}>{t('reports_view', 'View')}</button>
        <button className={styles.actionBtnSecondary} type="button" onClick={handleExport} disabled={rows.length === 0}>{t('reports_export_excel', 'Export Excel')}</button>
      </div>

      {loading ? (
        <p>{t('user_report_loading', 'Loading user activity report...')}</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          {reportType === 'summary' ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('reports_user', 'User')}</th>
                    <th>{t('users_role', 'Role')}</th>
                    <th>{t('teams_page_title', 'Teams')}</th>
                    <th>{t('reports_total', 'Total')}</th>
                    <th>{t('tasks_status_completed', 'Completed')}</th>
                    <th>{t('tasks_status_in_progress', 'In Progress')}</th>
                    <th>{t('tasks_status_pending', 'Pending')}</th>
                    <th>{t('tasks_status_cancelled', 'Cancelled')}</th>
                    <th>{t('user_report_completion_pct', 'Completion %')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={9} className={styles.empty}>{t('user_report_no_data', 'No user activity data found.')}</td></tr>
                  ) : rows.map((row) => {
                    const total = Number(row.total_tasks);
                    const pct = total > 0 ? Math.round((Number(row.completed) / total) * 100) : 0;
                    return (
                      <tr key={row.user_id}>
                        <td><strong>{row.username}</strong></td>
                        <td><span className={`${styles.roleBadge} ${ROLE_COLOR[row.role] || ''}`}>{row.role}</span></td>
                        <td>{row.teams || '-'}</td>
                        <td><strong>{total}</strong></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_completed}`}>{row.completed}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_in_progress}`}>{row.in_progress}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_pending}`}>{row.pending}</span></td>
                        <td><span className={`${styles.statusBadge} ${styles.status_cancelled}`}>{row.cancelled}</span></td>
                        <td>
                          <div className={styles.pctBar}>
                            <div className={styles.pctFill} style={{ width: `${pct}%` }} />
                            <span className={styles.pctLabel}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.detailCardsWrap}>
              {rows.length === 0 ? (
                <p className={styles.empty}>{t('user_report_no_data', 'No user activity data found.')}</p>
              ) : rows.map((user) => (
                <div key={user.user_id} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{user.username}</span>
                    <div className={styles.detailCardBadges}>
                      <span className={`${styles.roleBadge} ${ROLE_COLOR[user.role] || ''}`}>{user.role}</span>
                      {user.teams && <span className={styles.teamsBadge}>{user.teams}</span>}
                    </div>
                  </div>
                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('dashboard_total_tasks', 'Total Tasks')}</span>
                        <span>{user.total_tasks}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_completed', 'Completed')}</span>
                        <span>{user.completed}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_in_progress', 'In Progress')}</span>
                        <span>{user.in_progress}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_status_pending', 'Pending')}</span>
                        <span>{user.pending}</span>
                      </div>
                    </div>

                    {Array.isArray(user.tasks) && user.tasks.length > 0 && (
                      <div className={styles.userTaskList}>
                        <div className={styles.userTaskListHeader}>{t('user_report_tasks_in_period', 'Tasks in period')}</div>
                        {user.tasks.map((task) => (
                          <div key={task.task_id} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_date', 'Date')}</span>
                              <span className={styles.userTaskValue}>{formatDate(task.task_date)}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_task', 'Task')}</span>
                              <span className={`${styles.userTaskValue} ${styles.userTaskTitle}`}>{task.title}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_team', 'Team')}</span>
                              <span className={styles.userTaskValue}>{task.team_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_status', 'Status')}</span>
                              <span className={`${styles.statusBadge} ${styles[`status_${task.status}`]}`}>
                                {task.status?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('tasks_label_priority', 'Priority')}</span>
                              {task.priority ? (
                                <span className={`${styles.priorityBadge} ${styles[`priority_${task.priority}`]}`}>
                                  {task.priority}
                                </span>
                              ) : (
                                <span className={styles.userTaskValue}>-</span>
                              )}
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('common_location', 'Location')}</span>
                              <span className={styles.userTaskValue}>{task.task_day_location_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('common_gps', 'GPS')}</span>
                              <span className={styles.userTaskValue}>{formatCoordinates(task.task_day_latitude, task.task_day_longitude) || '-'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(!Array.isArray(user.tasks) || user.tasks.length === 0) && (
                      <p className={styles.userNoTasks}>{t('user_report_no_tasks_period', 'No tasks assigned in this period.')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserReportPage;
